import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Divider,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useTheme } from '@mui/material/styles';
import api, { DEGO_SCAN } from '../../infrastructure/config/api';
import { useSnackbar } from 'notistack';
import { watchlistService } from '../../application/services/watchlist-service';

function formatDateTime(value) {
  if (!value) {
    return '-';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '-';
  }

  return date.toLocaleString();
}

function openTradingView(symbol) {
  const tradingViewSymbol = `BINANCE:${String(symbol || '').toUpperCase()}.P`;
  const url = `https://www.tradingview.com/chart/?symbol=${encodeURIComponent(tradingViewSymbol)}`;
  window.open(url, '_blank', 'noopener,noreferrer');
}

function formatMarketCategories(categories) {
  if (!Array.isArray(categories) || categories.length === 0) {
    return '-';
  }

  return categories.join(', ');
}

function sortRowsByTriggerTimeDesc(rows) {
  if (!Array.isArray(rows) || rows.length === 0) {
    return [];
  }

  return [...rows].sort((a, b) => {
    const timeA = new Date(a?.triggerCandleTime).getTime();
    const timeB = new Date(b?.triggerCandleTime).getTime();
    const safeTimeA = Number.isNaN(timeA) ? Number.MAX_SAFE_INTEGER : timeA;
    const safeTimeB = Number.isNaN(timeB) ? Number.MAX_SAFE_INTEGER : timeB;

    if (safeTimeA !== safeTimeB) {
      return safeTimeB - safeTimeA;
    }

    return (b?.score || 0) - (a?.score || 0);
  });
}

function getScanErrorMessage(error, fallbackMessage) {
  const status = error?.response?.status;
  const code = error?.code;
  if (status === 504 || code === 'ECONNABORTED') {
    return 'DEGO scan đang bận hoặc quá thời gian xử lý, vui lòng thử lại.';
  }
  return error?.response?.data?.error || fallbackMessage;
}

function mapScanData(payload = {}) {
  return {
    timeframe: payload.timeframe || '15m',
    generatedAt: payload.generatedAt || null,
    scannedSymbols: payload.scannedSymbols || 0,
    totalSymbols: payload.totalSymbols || 0,
    skippedSymbols: payload.skippedSymbols || 0,
    topBuy: payload.topBuy || [],
    topSell: payload.topSell || [],
  };
}

function SignalActions({ row, watched, busy, onAdd, onRemove }) {
  return (
    <Stack direction="row" spacing={0.5} justifyContent="flex-end">
      <Tooltip title="Mở chart">
        <IconButton size="small" color="primary" onClick={() => openTradingView(row.symbol)} aria-label={`Mở chart ${row.symbol}`}>
          <OpenInNewIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title={watched ? 'Xóa khỏi danh sách symbols' : 'Thêm vào danh sách symbols'}>
        <span>
          <IconButton
            size="small"
            color={watched ? 'error' : (row.side === 'BUY' ? 'success' : 'error')}
            onClick={() => watched ? onRemove?.(row) : onAdd?.(row)}
            disabled={busy}
            aria-label={watched ? `Xóa ${row.symbol}` : `Thêm ${row.symbol}`}
          >
            {busy ? <CircularProgress size={18} color="inherit" /> : (watched ? <DeleteOutlineIcon fontSize="small" /> : <AddIcon fontSize="small" />)}
          </IconButton>
        </span>
      </Tooltip>
    </Stack>
  );
}

function MobileSignalCard({ row, onAdd, onRemove, watched, busy }) {
  return (
    <Paper
      sx={{
        p: 1.5,
        background: '#121826',
        border: '1px solid #27304a',
        borderRadius: 2,
      }}
    >
      <Stack spacing={1.25}>
        <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
          <Typography variant="body1" fontWeight={700}>
            {row.symbol}
          </Typography>
          <Chip
            label={row.side}
            color={row.side === 'BUY' ? 'success' : 'error'}
            size="small"
          />
        </Stack>

        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Chip label={`Score ${row.score}`} size="small" variant="outlined" />
          <Chip label={`Age ${row.ageBars}`} size="small" variant="outlined" />
        </Stack>

        <Stack spacing={0.5}>
          <Typography variant="caption" color="text.secondary">
            Group: {formatMarketCategories(row.marketCategories)}
          </Typography>
          <Typography variant="caption" color="text.secondary">Entry: {row.entryPrice}</Typography>
          <Typography variant="caption" color="text.secondary">SL: {row.stopLoss}</Typography>
          <Typography variant="caption" color="text.secondary">TP: {row.takeProfit}</Typography>
          <Typography variant="caption" color="text.secondary">
            Trigger: {formatDateTime(row.triggerCandleTime)}
          </Typography>
        </Stack>

        <Divider sx={{ borderColor: '#27304a' }} />

        <Stack spacing={0.5}>
          {(row.reason || []).map((item) => (
            <Typography key={item} variant="caption" color="text.secondary">
              {item}
            </Typography>
          ))}
        </Stack>

        <SignalActions row={row} watched={watched} busy={busy} onAdd={onAdd} onRemove={onRemove} />
      </Stack>
    </Paper>
  );
}

function SignalTable({ title, side, rows, mobile, onAdd, onRemove, watchedSymbols, actionLoadingKey }) {
  const sortedRows = sortRowsByTriggerTimeDesc(rows);

  return (
    <Paper
      sx={{
        background: '#171c29',
        border: '1px solid #27304a',
        borderRadius: 2,
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          px: 2,
          py: 1.5,
          borderBottom: '1px solid #27304a',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Typography variant="h6">{title}</Typography>
        <Chip
          label={`${sortedRows.length} results`}
          color={side === 'BUY' ? 'success' : 'error'}
          variant="outlined"
          size="small"
        />
      </Box>

      {mobile ? (
        <Stack spacing={1.25} sx={{ p: 1.25 }}>
          {sortedRows.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>
              No signal found.
            </Typography>
          ) : (
            sortedRows.map((row) => (
              <MobileSignalCard
                key={`${row.symbol}-${row.triggerCandleTime}-${row.side}`}
                row={row}
                onAdd={onAdd}
                onRemove={onRemove}
                watched={watchedSymbols.has(row.symbol)}
                busy={!!actionLoadingKey}
              />
            ))
          )}
        </Stack>
      ) : (
        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table size="small" sx={{ minWidth: 960 }}>
            <TableHead>
              <TableRow>
                <TableCell>Symbol</TableCell>
                <TableCell>Score</TableCell>
                <TableCell>Age</TableCell>
                <TableCell>Group</TableCell>
                <TableCell>Entry</TableCell>
                <TableCell>SL</TableCell>
                <TableCell>TP</TableCell>
                <TableCell>Trigger</TableCell>
                <TableCell>Reason</TableCell>
                <TableCell align="right">Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedRows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10} align="center">
                    No signal found.
                  </TableCell>
                </TableRow>
              ) : (
                sortedRows.map((row) => (
                  <TableRow key={`${row.symbol}-${row.triggerCandleTime}-${row.side}`}>
                    <TableCell>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography variant="body2" fontWeight={700}>
                          {row.symbol}
                        </Typography>
                        <Chip
                          label={row.side}
                          color={row.side === 'BUY' ? 'success' : 'error'}
                          size="small"
                        />
                      </Stack>
                    </TableCell>
                    <TableCell>{row.score}</TableCell>
                    <TableCell>{row.ageBars}</TableCell>
                    <TableCell>{formatMarketCategories(row.marketCategories)}</TableCell>
                    <TableCell>{row.entryPrice}</TableCell>
                    <TableCell>{row.stopLoss}</TableCell>
                    <TableCell>{row.takeProfit}</TableCell>
                    <TableCell>{formatDateTime(row.triggerCandleTime)}</TableCell>
                    <TableCell>
                      <Stack spacing={0.5}>
                        {(row.reason || []).map((item) => (
                          <Typography key={item} variant="caption" color="text.secondary">
                            {item}
                          </Typography>
                        ))}
                      </Stack>
                    </TableCell>
                    <TableCell align="right">
                      <SignalActions row={row} watched={watchedSymbols.has(row.symbol)} busy={!!actionLoadingKey} onAdd={onAdd} onRemove={onRemove} />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Paper>
  );
}

const DegoScanPage = () => {
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [data, setData] = useState({
    timeframe: '15m',
    generatedAt: null,
    scannedSymbols: 0,
    topBuy: [],
    topSell: [],
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isScanning, setIsScanning] = useState(false);
  const [actionLoadingKey, setActionLoadingKey] = useState('');
  const [watchedSymbols, setWatchedSymbols] = useState(new Set());
  const [scanError, setScanError] = useState('');

  const loadLatest = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await api.get(DEGO_SCAN.LATEST);
      const payload = response.data?.data || {};
      setScanError('');
      setData(mapScanData(payload));
      setIsScanning(payload.scan?.status === 'running');
    } catch (error) {
      console.error('Failed to load DEGO scan results:', error);
      const message = getScanErrorMessage(error, 'Failed to load scan results');
      setScanError(message);
      enqueueSnackbar(message, { variant: 'error' });
    } finally {
      setIsLoading(false);
    }
  }, [enqueueSnackbar]);

  const loadWatchlist = useCallback(async () => {
    try {
      const items = await watchlistService.list();
      setWatchedSymbols(new Set(items.map((item) => String(item.symbol).toUpperCase())));
    } catch (error) {
      console.error('Failed to load watchlist:', error);
    }
  }, []);

  const runScan = useCallback(async () => {
    try {
      setIsScanning(true);
      const response = await api.post(DEGO_SCAN.RUN);
      setScanError('');
      enqueueSnackbar(
        response.data?.accepted ? 'Scan đã bắt đầu chạy nền' : 'Scan đang chạy',
        { variant: 'info' }
      );
    } catch (error) {
      console.error('Failed to run DEGO scan:', error);
      const message = getScanErrorMessage(error, 'Failed to run scan');
      setScanError(message);
      setIsScanning(false);
      enqueueSnackbar(message, { variant: 'error' });
    }
  }, [enqueueSnackbar]);

  useEffect(() => {
    loadLatest();
    loadWatchlist();
  }, [loadLatest, loadWatchlist]);

  useEffect(() => {
    if (!isScanning) return undefined;

    const intervalId = setInterval(async () => {
      try {
        const response = await api.get(DEGO_SCAN.LATEST);
        const payload = response.data?.data || {};
        const status = payload.scan?.status;

        if (status === 'running') return;

        setData(mapScanData(payload));
        setIsScanning(false);
        if (status === 'completed') {
          setScanError('');
          enqueueSnackbar('Scan completed', { variant: 'success' });
        } else if (status === 'failed') {
          const message = payload.scan?.error || 'Failed to run scan';
          setScanError(message);
          enqueueSnackbar(message, { variant: 'error' });
        }
      } catch (error) {
        console.error('Failed to poll DEGO scan:', error);
      }
    }, 2000);

    return () => clearInterval(intervalId);
  }, [enqueueSnackbar, isScanning]);

  const handleAddToWatchlist = useCallback(async (row) => {
    const key = `${row.symbol}-${row.triggerCandleTime}-${row.side}`;
    try {
      if (watchedSymbols.has(row.symbol)) {
        enqueueSnackbar(`${row.symbol} đã tồn tại trong danh sách symbols`, { variant: 'warning' });
        return;
      }
      setActionLoadingKey(key);
      const watchSide = row.side === 'BUY' ? 'LONG' : 'SHORT';
      await watchlistService.add(row.symbol, watchSide, 'DEGO');
      setWatchedSymbols((current) => new Set(current).add(row.symbol));
      enqueueSnackbar(`Đã thêm ${row.symbol} ${watchSide} vào danh sách symbols`, { variant: 'success' });
    } catch (error) {
      console.error('Failed to add DEGO signal to watchlist:', error);
      enqueueSnackbar(error.response?.data?.error || 'Không thể thêm symbol vào danh sách', { variant: 'error' });
    } finally {
      setActionLoadingKey('');
    }
  }, [enqueueSnackbar, watchedSymbols]);

  const handleRemoveFromWatchlist = useCallback(async (row) => {
    const key = `${row.symbol}-${row.triggerCandleTime}-${row.side}`;
    try {
      setActionLoadingKey(key);
      await watchlistService.remove(row.symbol);
      setWatchedSymbols((current) => {
        const next = new Set(current);
        next.delete(row.symbol);
        return next;
      });
      enqueueSnackbar(`Đã xóa ${row.symbol} khỏi danh sách symbols`, { variant: 'success' });
    } catch (error) {
      enqueueSnackbar(error.response?.data?.error || 'Không thể xóa symbol khỏi danh sách', { variant: 'error' });
    } finally {
      setActionLoadingKey('');
    }
  }, [enqueueSnackbar]);

  return (
    <Container maxWidth="xl" sx={{ py: { xs: 2, md: 3 }, px: { xs: 1.5, md: 3 } }}>
      <Stack spacing={3}>
        <Paper
          sx={{
            p: { xs: 2, md: 3 },
            background: 'linear-gradient(135deg, #111827 0%, #182235 100%)',
            border: '1px solid #27304a',
            borderRadius: 2,
          }}
        >
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={2}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', md: 'center' }}
          >
            <Box>
              <Typography variant={isMobile ? 'h5' : 'h4'} fontWeight={700}>
                DEGO Exact Scanner
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Binance Futures USDT-M {data.timeframe} | Latest exact-form BUY and SELL scan
              </Typography>
            </Box>

            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1}
              alignItems={{ xs: 'stretch', sm: 'center' }}
              sx={{ width: { xs: '100%', md: 'auto' } }}
            >
              <Chip label={`Scanned ${data.scannedSymbols} symbols`} variant="outlined" />
              {data.totalSymbols ? (
                <Chip
                  label={`Skipped ${data.skippedSymbols || 0}/${data.totalSymbols}`}
                  variant="outlined"
                />
              ) : null}
              <Chip
                label={`Updated ${formatDateTime(data.generatedAt)}`}
                variant="outlined"
                sx={{ maxWidth: { xs: '100%', sm: 'none' } }}
              />
              <Button
                variant="contained"
                startIcon={isScanning ? <CircularProgress size={16} color="inherit" /> : <AutorenewIcon />}
                onClick={runScan}
                disabled={isScanning}
                fullWidth={isMobile}
              >
                {isScanning ? 'Scanning...' : 'Scan'}
              </Button>
            </Stack>
          </Stack>
        </Paper>
        {scanError ? (
          <Alert
            severity="warning"
            action={(
              <Button
                color="inherit"
                size="small"
                onClick={loadLatest}
                disabled={isLoading || isScanning}
              >
                Thử lại
              </Button>
            )}
          >
            {scanError}
          </Alert>
        ) : null}

        {isLoading ? (
          <Box sx={{ py: 12, textAlign: 'center' }}>
            <CircularProgress />
          </Box>
        ) : (
          <Stack spacing={3}>
            <SignalTable
              title="BUY Signals"
              side="BUY"
              rows={data.topBuy}
              mobile={isMobile}
              onAdd={handleAddToWatchlist}
              onRemove={handleRemoveFromWatchlist}
              watchedSymbols={watchedSymbols}
              actionLoadingKey={actionLoadingKey}
            />
            <SignalTable
              title="SELL Signals"
              side="SELL"
              rows={data.topSell}
              mobile={isMobile}
              onAdd={handleAddToWatchlist}
              onRemove={handleRemoveFromWatchlist}
              watchedSymbols={watchedSymbols}
              actionLoadingKey={actionLoadingKey}
            />
          </Stack>
        )}
      </Stack>
    </Container>
  );
};

export default DegoScanPage;
